import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check, Crown, ShieldCheck, ArrowRight, BadgeDollarSign, Newspaper, Building2, ChartLine } from "lucide-react";
import { api } from "../lib/api";
import HostApplicationForm from "../components/HostApplicationForm";

const TIER_ICONS = { bronze: Building2, silver: Sparkles, gold: Crown };

export default function HostTiersPage() {
  const [catalog, setCatalog] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/host-memberships")
      .then((r) => setCatalog(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="host-tiers-page">
      {/* Hero */}
      <div>
        <div className="overline">
          <span className="text-[var(--brand-red)]">Janté Host Network</span>
          <span className="mx-2 opacity-60">·</span>
          Early-adopter window
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-[var(--emerald)] mt-5 leading-[1.05]">
          Host tiers that work<br />
          <em className="not-italic text-[var(--gold)]">while you don't.</em>
        </h1>
        <p className="text-[var(--ink-2)] mt-5 max-w-2xl text-lg italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          "Watch The Results, and Then Subscribe."
        </p>
        <p className="text-[var(--ink-2)] mt-3 max-w-2xl">
          Sasha guides guests to Janté hosts — we advertise, promote, and coordinate.
          <strong className="text-[var(--emerald)]"> Sign up by July 1, 2026</strong> and your
          first <strong className="text-[var(--emerald)]">6 months are free</strong>. All tiers
          earn on a <strong className="text-[var(--brand-red)]">20% commission</strong> of bookings.
          {catalog?.launch_special?.uplift_estimate_label && (
            <> Expect a <strong className="text-[var(--brand-red)]">{catalog.launch_special.uplift_estimate_label.replace("Estimated ", "")}</strong>.</>
          )}
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.2em]">
          <span className="px-3 py-1.5 border border-[var(--gold)] text-[var(--emerald)]">6 months FREE — early adopters</span>
          <span className="px-3 py-1.5 border border-[var(--gold)] text-[var(--emerald)]">20% commission · all tiers</span>
          <span className="px-3 py-1.5 border border-[var(--gold)] text-[var(--emerald)]">Est. 20–30% occupancy lift</span>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {(catalog?.tiers || []).map((t) => {
          const Icon = TIER_ICONS[t.id] || Building2;
          const isGold = t.id === "gold";
          return (
            <div
              key={t.id}
              className={`relative ${isGold ? "lux-card-dark" : "lux-card"} flex flex-col`}
              data-testid={`host-tier-card-${t.id}`}
            >
              <Icon size={22} strokeWidth={1.25} className="text-[var(--gold)]" />
              <div className="overline mt-3" style={{ fontSize: "0.72rem" }}>{t.tagline}</div>
              <h3 className={`font-display text-3xl mt-2 ${isGold ? "text-[var(--gold)]" : "text-[var(--emerald)]"}`}>
                {t.name}
              </h3>
              <div className="mt-5 flex items-baseline gap-2">
                <span className={`font-display text-4xl ${isGold ? "text-[var(--gold)]" : "text-[var(--emerald)]"}`}>
                  ${t.price.toLocaleString()}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-2)]">
                  / month
                </span>
              </div>
              <div className="text-[11px] mt-2 space-y-0.5 text-[var(--ink-2)]">
                {t.price_mtm != null && (
                  <div>or <strong>${t.price_mtm.toLocaleString()}</strong> month-to-month</div>
                )}
                {t.price_yearly != null && (
                  <div>or <strong>${t.price_yearly.toLocaleString()}</strong> /year (save)</div>
                )}
              </div>
              <div className={`text-[10px] uppercase tracking-[0.2em] mt-3 ${isGold ? "text-[var(--gold)]" : "text-[var(--brand-red)]"}`}>
                ${t.setup_fee?.toLocaleString() || "—"} setup fee · 20% commission
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] mt-1 text-[var(--emerald)]">
                <strong>6 months FREE</strong> · if signed by Jul 1, 2026
              </div>
              <ul className="mt-6 space-y-2.5 flex-1 text-sm">
                {t.features.map((f) => {
                  const isInheritance = f.toLowerCase().startsWith("plus everything in");
                  return (
                    <li
                      key={f}
                      className={`flex items-start gap-2 text-[var(--ink-2)] ${
                        isInheritance ? "italic font-medium" : ""
                      }`}
                    >
                      <Check
                        size={15}
                        strokeWidth={1.5}
                        className={`shrink-0 mt-0.5 ${
                          isInheritance
                            ? "text-[var(--brand-red)]"
                            : isGold
                            ? "text-[var(--gold)]"
                            : "text-[var(--emerald)]"
                        }`}
                      />
                      <span
                        className={
                          isInheritance
                            ? isGold
                              ? "text-[var(--gold)]"
                              : "text-[var(--brand-red)]"
                            : ""
                        }
                      >
                        {f}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <button
                onClick={() => {
                  setSelectedTier(t.id);
                  setTimeout(() => {
                    document.getElementById("host-tier-apply")?.scrollIntoView({ behavior: "smooth" });
                  }, 80);
                }}
                className={`mt-8 w-full ${isGold ? "btn-gold" : "btn-primary"}`}
                data-testid={`host-tier-apply-${t.id}`}
              >
                Apply for {t.name}
                <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Additional programs CTA strip */}
      <div className="grid md:grid-cols-2 gap-5 mt-14">
        <button
          onClick={() => navigate("/hosts/property-assistance")}
          className="lux-card text-left transition-all hover:-translate-y-0.5"
          data-testid="pa-cta"
        >
          <BadgeDollarSign size={22} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="overline mt-3">JTL Property Assistance</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Let us run it.</h3>
          <p className="text-sm text-[var(--ink-2)] mt-3">
            Hassle-free STR ownership — Janté hosts or co-hosts your property at 10% of rental revenue.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--emerald)]">
            Learn more <ArrowRight size={11} strokeWidth={1.5} />
          </div>
        </button>
        <button
          onClick={() => navigate("/hosts/rental-to-rent")}
          className="lux-card text-left transition-all hover:-translate-y-0.5"
          data-testid="r2r-cta"
        >
          <Newspaper size={22} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="overline mt-3">Rental-to-Rent</div>
          <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">We lease. You earn.</h3>
          <p className="text-sm text-[var(--ink-2)] mt-3">
            Janté leases eligible STR-licensed properties directly from landlords and pays 1.5% rental rates — zero operational lift.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--emerald)]">
            Learn more <ArrowRight size={11} strokeWidth={1.5} />
          </div>
        </button>
      </div>

      {/* Apply form */}
      <div id="host-tier-apply" className="mt-20">
        <div className="overline">Apply</div>
        <h2 className="font-display text-4xl text-[var(--emerald)] mt-2">
          A few details, then a human reaches out.
        </h2>
        <HostApplicationForm
          program="host_tier"
          initialTier={selectedTier}
          title={selectedTier ? `Apply for ${(catalog?.tiers || []).find(x => x.id === selectedTier)?.name}` : "Select a tier above — or apply and we'll recommend"}
          cta={selectedTier ? `Apply for ${selectedTier[0].toUpperCase()}${selectedTier.slice(1)}` : "Submit application"}
        />
      </div>

      <div className="mt-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <ChartLine size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
        Est. 20–30% lift in ratings, rates, and occupancy for Janté-listed properties.
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <ShieldCheck size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
        Early-adopter pricing locked in at application. 6 months free if signed by July 1, 2026.
      </div>
    </div>
  );
}
